import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, HeartPulse, ChevronLeft } from 'lucide-react';
import { usePets } from '../../contexts/PetsContext';
import { useHealth } from '../../contexts/HealthContext';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { ROUTES } from '../../config/constants';
import HealthRecordCard from './components/HealthRecordCard';

/**
 * Timeline de observaciones de una mascota.
 * Desc por occurredAt. Botón para agregar nueva.
 */

const PetHealthPage = () => {
  const { petId } = useParams();
  const navigate = useNavigate();
  const { pets, status: petsStatus } = usePets();
  const { getRecordsForPet, status: healthStatus } = useHealth();

  const isLoading = petsStatus === 'loading' || healthStatus === 'loading';
  const pet = pets.find((p) => p.id === petId);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <div className="h-12 animate-pulse rounded-2xl bg-slate-100" />
        <div className="h-20 animate-pulse rounded-2xl bg-slate-100" />
        <div className="h-20 animate-pulse rounded-2xl bg-slate-100" />
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-bold text-slate-900">Mascota no encontrada</h1>
        <Card>
          <p className="text-sm text-slate-600">
            Esta mascota no existe o fue eliminada.
          </p>
          <div className="mt-3">
            <Button variant="secondary" fullWidth onClick={() => navigate(ROUTES.HEALTH)}>
              Volver
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const records = getRecordsForPet(petId);
  const newPath = ROUTES.HEALTH_RECORD_NEW.replace(':petId', petId);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link
          to={ROUTES.HEALTH}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Salud
        </Link>
        <div className="mt-1 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <HeartPulse className="h-6 w-6 text-rose-700" aria-hidden="true" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{pet.name}</h1>
              <p className="text-sm text-slate-500">
                {records.length === 0
                  ? 'Sin observaciones'
                  : `${records.length} observación${records.length === 1 ? '' : 'es'}`}
              </p>
            </div>
          </div>
          <Button
            as={Link}
            to={newPath}
            variant="primary"
            size="sm"
            leftIcon={Plus}
          >
            Nueva
          </Button>
        </div>
      </div>

      {records.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <HeartPulse className="h-10 w-10 text-slate-300" aria-hidden="true" />
            <div>
              <p className="font-medium text-slate-800">Sin observaciones todavía</p>
              <p className="mt-1 text-sm text-slate-500">
                Anotá el primer peso, vacuna o control de {pet.name}.
              </p>
            </div>
            <Button as={Link} to={newPath} variant="primary" leftIcon={Plus}>
              Agregar la primera
            </Button>
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {records.map((r) => (
            <HealthRecordCard key={r.id} record={r} petId={petId} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PetHealthPage;
